import {
  GradientText,
  RightIcon,
  SuccessIcon,
} from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import {
  STATUS_TONE_COLOR,
  operationDisplayStatusKey,
  operationDisplayTone,
  type OperationStatusTone,
} from "../utils/operationStatus";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../assets/icons";
import type { IOperationActionRequired } from "../services/api";
import { useTranslation } from "react-i18next";

export interface TransactionItemProps {
  status: string;
  amount: string;
  currency: string;
  date: string;
  actionRequired?: IOperationActionRequired | null;
  senderName?: string;
  cashDepositPending?: boolean;
  depositName?: string;
  onDeposit?: () => void;
  /** Invoked when the user taps a regular (non cash-deposit) transaction row. */
  onSelect?: () => void;
}

/** Initials of first + last name, e.g. "Jane Doe" -> "JD". */
const getInitials = (name?: string): string => {
  const parts = (name || "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

const statusIcon = (tone: OperationStatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    case "action_required":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  status,
  amount,
  currency,
  date,
  actionRequired = null,
  senderName,
  cashDepositPending = false,
  depositName,
  onDeposit,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn, dropzoneColors } = colorConfig;

  // CASH_DEPOSIT operation still awaiting an in-person deposit: a prominent,
  // tappable card inviting the user to complete the deposit.
  if (cashDepositPending) {
    const accent = dropzoneColors?.[0] ?? textColorBtn;
    return (
      <button
        type="button"
        className="transactions-history-item transactions-deposit-item"
        style={{ background: backgroundBtn }}
        onClick={onDeposit}
      >
        <span
          className="transactions-avatar transactions-deposit-avatar"
          style={{ background: textColorBtn, color: backgroundBtn }}
        >
          {getInitials(depositName)}
        </span>
        <div className="transactions-deposit-info">
          <GradientText className="transaction-title" textColor={textColorBtn}>
            <b>{depositName}</b>
          </GradientText>
          <div className="transactions-deposit-action">
            <GradientText
              className="transactions-deposit-cta"
              textColor={textColorBtn}
            >
              {t("transactions.deposit")}
            </GradientText>
            <span
              className="transactions-deposit-arrow"
              style={{ background: accent }}
            >
              <RightIcon size={32} colors={[textColorBtn, textColorBtn]} />
            </span>
          </div>
        </div>
      </button>
    );
  }

  const displayOp = { status, actionRequired };
  const tone = operationDisplayTone(displayOp);
  const toneColor = STATUS_TONE_COLOR[tone];
  const label = t(operationDisplayStatusKey(displayOp));
  const selectable = typeof onSelect === "function";

  return (
    <section
      className="transactions-history-item"
      onClick={selectable ? onSelect : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      style={selectable ? { cursor: "pointer" } : undefined}
    >
      <p className="transactions-avatar">
        {senderName ? getInitials(senderName) : t("transactions.avatar")}
      </p>
      <section className="transactions-info">
        <div className="transactions-row transactions-row--primary">
          <GradientText className="transaction-title" textColor={bodyCopy}>
            <b>{senderName || t("transactions.transfers")}</b>
          </GradientText>
          <GradientText
            className="transactions-amount"
            textColor={bodyCopy}
            as="span"
          >
            ${amount} {currency}
          </GradientText>
        </div>
        <div className="transactions-row transactions-row--meta">
          <section className="transactions-status">
            {statusIcon(tone, toneColor)}
            <GradientText textColor={toneColor} as="span">
              {label}
            </GradientText>
          </section>
          <GradientText className="transactions-date" textColor={`${bodyCopy}5E`}>
            {date}
          </GradientText>
        </div>
      </section>
    </section>
  );
};
